import { useEffect } from 'react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import SalaryOutcomesSection from '../components/landing/SalaryOutcomesSection.jsx';
import WhyOthersFailSection from '../components/landing/WhyOthersFailSection.jsx';
import AdaptationFlowSection from '../components/landing/AdaptationFlowSection.jsx';
import LiveJobsSection from '../components/landing/LiveJobsSection.jsx';
import HowItWorksSection from '../components/landing/HowItWorksSection.jsx';
import PricingSection from '../components/landing/PricingSection.jsx';
import FaqAndFinalCtaSection from '../components/landing/FaqAndFinalCtaSection.jsx';
import { track } from '../utils/track.js';

// The full public landing surface. Voice is human-observational ("noticed",
// "responds", "different") — never architectural.
export default function LandingPage() {
  useEffect(() => { track('landing_view'); }, []);

  return (
    <div className="lp-root">
      <div className="lp-glow" aria-hidden="true" />
      <Header />

      <main>
        {/* Hero copy stack: headline → primary CTA → subheadline → proof →
            Watch Demo button (scrolls to #demo). The product demo video
            lives in the feature card below (moved out of the hero). */}
        <section className="lp-hero">
          <h1 className="lp-h1 lp-area-headline">
            Stop Watching. Start Learning.
          </h1>

          <a href="/registration" className="lp-btn-primary lp-area-primary">
            Try For Free
          </a>

          <h2 className="lp-h2 lp-area-subhead">
            AdaptOS gets you certified without the hours of videos and the guesswork.
            <br />
            Teach. Test. Repair. Apply.
          </h2>

          <p className="lp-proof lp-area-proof">
            No Credit Card Required to Start
          </p>

          {/* Scrolls down to the demo video in the feature card below.
              Smooth scroll is handled by html { scroll-behavior: smooth }. */}
          <a href="#demo" className="lp-btn-watch lp-area-watch" aria-label="Watch the demo below">
            <span aria-hidden="true">▶</span> Watch Demo
          </a>
        </section>

        {/* Second card — the product demo video on its own, enlarged. The hero
            "Watch Demo" button scrolls here (#demo). */}
        <section className="lp-demo-section">
          <div className="lp-feature-video" id="demo">
            <div className="lp-video-frame">
              <video
                src="/FinalDemo.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/investor/screenshots/CRA1.png"
                style={{ width: '100%', borderRadius: '12px', display: 'block' }}
              />
            </div>
            <p className="lp-video-label">Captured By Beta User</p>
          </div>
        </section>

        {/* Third card — Roadmap + Career Hub copy, below the demo. */}
        <section className="lp-feature">
          <h2 className="lp-feature-h2">
            AdaptOS stays with you from confused to certified to career.
          </h2>
          <p className="lp-feature-body">
            Our Roadmap and Career Hub help you find the role that's right for you,
            then builds the path to that role so you know where your career is going
            and how to get there. You'll see live openings, job descriptions, and real
            salaries.
            <br />
            <br />
            <br />
            <strong>Come Confused. Get Certified. Start Your Career.</strong>
          </p>
        </section>

        <SalaryOutcomesSection />
        <LiveJobsSection />
        <PricingSection />
        <AdaptationFlowSection />
        <WhyOthersFailSection />
        <HowItWorksSection />
        <FaqAndFinalCtaSection />
      </main>

      <Footer />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300..600&family=Hanken+Grotesk:wght@300;400;500;600;700&display=swap');

        html { scroll-behavior: smooth; }

        .lp-root {
          background: #f6f7f9; color: #111110;
          min-height: 100vh; position: relative; overflow-x: hidden;
          font-family: 'Hanken Grotesk', sans-serif;
          /* Scale the whole landing surface 20% larger so the product demo
             video is legible and content fills more of the viewport. Single knob —
             tune here if 1.2 ever feels off. */
          zoom: 1.2;
        }
        .lp-glow {
          position: fixed; top: -22%; left: 50%; transform: translateX(-50%);
          width: min(1000px, 100%); height: 720px; max-width: 100%;
          background: radial-gradient(ellipse at center, rgba(217,119,6,0.08) 0%, rgba(217,119,6,0.03) 45%, transparent 70%);
          filter: blur(20px); pointer-events: none; z-index: 0;
        }
        /* DESKTOP HERO ─────────────────────────────────────────────
           Two-column grid via named areas. Copy stacks in the left column;
           the right column is intentionally empty now that the demo video
           has moved to the feature card below. align-items: center keeps
           the copy vertically centered. */
        .lp-hero {
          position: relative; z-index: 1;
          max-width: 1200px; margin: 0 auto;
          min-height: 80vh;
          padding: clamp(40px, 7vh, 88px) clamp(16px, 4vw, 40px);
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          grid-template-areas:
            "headline"
            "primary"
            "subhead"
            "proof"
            "watch";
          column-gap: clamp(32px, 5vw, 72px);
          row-gap: 0;
          align-items: center;
        }
        .lp-area-headline  { grid-area: headline; }
        .lp-area-primary   { grid-area: primary;   margin-top: 28px; justify-self: start; }
        .lp-area-subhead   { grid-area: subhead; }
        .lp-area-proof     { grid-area: proof; }
        .lp-area-watch     { grid-area: watch; margin-top: 22px; justify-self: start; }

        /* DESKTOP — center the hero copy horizontally within the card.
           Scoped to >880px so the mobile stack (left-aligned, full-width
           CTAs) is untouched. margin-inline: auto centers the width-capped
           text blocks; justify-self centers the CTAs in their column. */
        @media (min-width: 881px) {
          .lp-area-headline,
          .lp-area-subhead,
          .lp-area-proof { text-align: center; }
          /* Compound selectors so these beat the later single-class
             .lp-h2 / .lp-proof base rules whose margin shorthand would
             otherwise reset the side margins to 0 and pin the (max-width)
             blocks to the left. */
          .lp-h2.lp-area-subhead,
          .lp-proof.lp-area-proof { margin-left: auto; margin-right: auto; }
          .lp-area-primary, .lp-area-watch { justify-self: center; }
        }

        .lp-h1 {
          font-family: 'Fraunces', serif; font-weight: 500;
          font-size: clamp(34px, 5.4vw, 62px); line-height: 1.06;
          letter-spacing: -0.02em; margin: 0; overflow-wrap: break-word;
        }
        .lp-h2 {
          font-weight: 300; color: #1a1a24;
          font-size: clamp(20px, 2.6vw, 24px); line-height: 1.55;
          margin: 22px 0 0; max-width: 46ch;
        }
        .lp-proof {
          font-size: 14px; color: rgba(17,17,16,0.55);
          margin: 14px 0 0; max-width: 50ch; line-height: 1.5;
        }

        .lp-btn-primary {
          display: inline-flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #ffffff; text-decoration: none;
          font-size: 16px; font-weight: 700; letter-spacing: .01em;
          padding: 15px 30px; border-radius: 12px;
          box-shadow: 0 6px 22px rgba(217,119,6,0.30);
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .lp-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(217,119,6,0.42); }

        /* Watch Demo — subordinate outline button; scrolls down to #demo.
           Kept visually quieter than the amber primary CTA. */
        .lp-btn-watch {
          display: inline-flex; align-items: center; justify-content: center; gap: 9px;
          background: transparent; color: #111110;
          border: 1.5px solid rgba(17,17,16,0.20);
          font-size: 15px; font-weight: 600; letter-spacing: .01em;
          padding: 13px 26px; border-radius: 12px; text-decoration: none;
          transition: border-color .18s ease, background .18s ease;
        }
        .lp-btn-watch:hover { border-color: rgba(17,17,16,0.45); background: rgba(17,17,16,0.04); }

        /* Product frame — subtle border + dark surface; no drop shadow
           that would obscure the UI inside the video. overflow: hidden
           keeps the rounded corners crisp around the video element. */
        .lp-video-frame {
          background: #ffffff;
          border: 1px solid rgba(45, 212, 191, 0.18);
          border-radius: 12px;
          overflow: hidden;
        }
        .lp-video-label {
          font-size: 12px; color: rgba(17,17,16,0.45);
          margin: 10px 0 0; letter-spacing: 0.01em; font-weight: 400;
        }

        /* DEMO CARD ─────────────────────────────────────────────────
           Second card — the product demo video on its own, enlarged. */
        .lp-demo-section {
          position: relative; z-index: 1;
          max-width: 1200px; margin: 0 auto;
          padding: clamp(28px, 5vh, 60px) clamp(16px, 4vw, 40px) 0;
          text-align: center;
        }
        /* Demo video — moved down from the hero. Enlarged and centered. */
        .lp-feature-video {
          max-width: 960px;
          margin: 0 auto;
          /* Offset the sticky site header when scrolled to via the #demo anchor. */
          scroll-margin-top: 150px;
        }

        /* FEATURE SPOTLIGHT ─────────────────────────────────────────
           Third card — Roadmap + Career Hub copy. Heading uses the same
           Fraunces display face as the hero. */
        .lp-feature {
          position: relative; z-index: 1;
          max-width: 1200px; margin: 0 auto;
          padding: clamp(32px, 6vh, 72px) clamp(16px, 4vw, 40px);
          text-align: center;
        }
        .lp-feature-h2 {
          font-family: 'Fraunces', serif; font-weight: 500;
          font-size: clamp(28px, 4vw, 46px); line-height: 1.12;
          letter-spacing: -0.02em; margin: 0 auto; max-width: 22ch;
        }
        .lp-feature-body {
          font-weight: 300; color: #1a1a24;
          font-size: clamp(17px, 2.2vw, 21px); line-height: 1.6;
          margin: 22px auto 0; max-width: 62ch;
        }

        @media (max-width: 880px) {
          /* MOBILE HERO ─────────────────────────────────────────────
             Single column. Reading order is enforced by the named-area
             stack: headline → primary CTA → video → subheadline →
             proof → microcopy. */
          .lp-hero {
            grid-template-columns: minmax(0, 1fr);
            grid-template-areas:
              "headline"
              "primary"
              "subhead"
              "proof"
              "watch";
            min-height: auto;
            padding-top: clamp(24px, 4vh, 40px);
            row-gap: 0;
          }
          .lp-area-primary   { margin-top: 22px; justify-self: stretch; }
          .lp-area-watch     { justify-self: stretch; }
        }
        @media (max-width: 560px) {
          /* Full-width tappable CTAs on small phones. */
          .lp-btn-primary, .lp-btn-watch { width: 100%; box-sizing: border-box; }
        }
      `}</style>
    </div>
  );
}
